import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SectionDivider, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import { toggleNestedValue } from '@functions/entity'

import useBrand, { useBrandForm } from '@hooks/useBrand'
import useCurrentUser from '@hooks/useCurrentUser'

import EntitySelector from '@components/EntitySelector'
import ScopedConditionalContent from '@components/ScopedConditionalContent'
import ToggleListItem from '@components/ToggleListItem'

const BrandSettingsModal = (props) => {
  const { callbacks, modalKey = 'BrandSettingsModal', showModal } = props
  const { closeModal, updateBrand } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { brand } = modalPayload

  const { isAdmin } = useCurrentUser()

  const {
    callbacks: { updateBrand: updateFn },
    updating,
  } = useBrand(brand)

  const brandFormPayload = useBrandForm(brand)
  const {
    entityState,
    entityState: {
      options,
      debtor_agency_id,
    },
    handlers,
    setEntityState,
  } = brandFormPayload

  const {
    artwork_builder_template_requests_enabled,
    cross_agency_collaboration,
    disable_artwork_download,
    disable_artwork_upload,
    disable_print_quality,
    realhub_platform,
    red_alert,
    require_template_specialists,
    template_designer,
    template_page_fee,
    template_page_monthly_allowance,
  } = options

  const updateBrandPayload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: 'Brand Updated',
  }

  return (
    <SidebarModal
      callbacks={callbacks}
      modalKey={modalKey}
      showModal={showModal}
      size="small"
    >
      <SidebarModal.Header callbacks={callbacks} title="Brand" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <Form>
            <Form.Field label="Template Page Fee">
              <input
                data-validate-field-on="change"
                name="options.template_page_fee"
                type="number"
                value={template_page_fee}
                {...handlers}
              />
            </Form.Field>

            <Form.Field label="Free Templates (Monthly)" marginTop="large">
              <input
                data-validate-field-on="change"
                name="options.template_page_monthly_allowance"
                type="number"
                value={template_page_monthly_allowance}
                {...handlers}
              />
            </Form.Field>

            <Form.Field marginTop="large" label="Realhub Platform">
              <select
                name="options.realhub_platform"
                value={realhub_platform}
                {...handlers}
              >
                <option value="standalone_v1">Standalone</option>
              </select>
            </Form.Field>

            <Form.Field marginTop="large">
              <EntitySelector
                searchRequestOptions={{
                  brand_id: brand.id,
                }}
                callbacks={{
                  selectItem: agency => setEntityState({ debtor_agency_id: agency ? agency.id : null }),
                }}
                entityKey="agencies"
                label="Debtor Agency"
                marginTop={['large', 0]}
                selectedItemId={debtor_agency_id}
              />
            </Form.Field>
          </Form>
        </ScopedConditionalContent>

        <SectionDivider
          boxProps={{ marginBottom: 'large', marginTop: isAdmin ? 'large' : '0' }}
        >
          ARTWORK SETTINGS
        </SectionDivider>

        <ToggleListItem
          onClick={() => toggleNestedValue(brandFormPayload, 'options.disable_print_quality', { shouldSetState: true })}
          toggleValue={!disable_print_quality || disable_print_quality === undefined}
        >
          <ToggleListItem.Title>Download Print Quality</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agencies under this brand group will be able to download print quality PDF
          </ToggleListItem.Description>
        </ToggleListItem>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <ToggleListItem
            onClick={() => (
              toggleNestedValue(brandFormPayload, 'options.disable_artwork_download', { shouldSetState: true })
            )}
            toggleValue={!disable_artwork_download || disable_artwork_download === undefined}
          >
            <ToggleListItem.Title>Artwork Download</ToggleListItem.Title>

            <ToggleListItem.Description>
              Agencies will not be able to download artwork unless they have a campaign fee set
            </ToggleListItem.Description>
          </ToggleListItem>
        </ScopedConditionalContent>

        <ToggleListItem
          onClick={() => (
            toggleNestedValue(brandFormPayload, 'options.disable_artwork_upload', { shouldSetState: true })
          )}
          toggleValue={!disable_artwork_upload || disable_artwork_upload === undefined}
        >
          <ToggleListItem.Title>Upload Artworks</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agencies will be able to upload custom artworks to orders
          </ToggleListItem.Description>
        </ToggleListItem>

        <SectionDivider boxProps={{ marginBottom: 'large' }}>FEATURES</SectionDivider>

        <ToggleListItem
          onClick={() => (
            toggleNestedValue(brandFormPayload, 'options.cross_agency_collaboration', { shouldSetState: true })
          )}
          toggleValue={!!cross_agency_collaboration}
        >
          <ToggleListItem.Title>Cross Agency Collaboration</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agencies can assign campaigns to agencies linked by brand
          </ToggleListItem.Description>
        </ToggleListItem>

        <ToggleListItem
          onClick={() => toggleNestedValue(brandFormPayload, 'options.template_designer', { shouldSetState: true })}
          toggleValue={!!template_designer}
        >
          <ToggleListItem.Title>Brand Template Management</ToggleListItem.Title>

          <ToggleListItem.Description>
            Brand can access and control template access with Brand Admin
          </ToggleListItem.Description>
        </ToggleListItem>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <ToggleListItem
            onClick={() => (
              toggleNestedValue(brandFormPayload, 'options.require_template_specialists', { shouldSetState: true })
            )}
            toggleValue={!!require_template_specialists}
          >
            <ToggleListItem.Title>Template Specialists</ToggleListItem.Title>

            <ToggleListItem.Description>
              Brand uses specific template specialists
            </ToggleListItem.Description>
          </ToggleListItem>
        </ScopedConditionalContent>

        <ToggleListItem
          boxProps={{ marginBottom: 0 }}
          onClick={() => (
            toggleNestedValue(
              brandFormPayload,
              'options.artwork_builder_template_requests_enabled',
              { shouldSetState: true },
            )
          )}
          toggleValue={!!artwork_builder_template_requests_enabled}
        >
          <ToggleListItem.Title>Artwork Builder Template Requests</ToggleListItem.Title>

          <ToggleListItem.Description>
            Agencies can access &#34;Request Change or Fix&#34; button within Artwork Builder
          </ToggleListItem.Description>
        </ToggleListItem>

        <ScopedConditionalContent permittedRoleTitles={['Admin', 'Support']}>
          <SectionDivider boxProps={{ marginY: 'large' }}>ADDITIONAL SETTINGS</SectionDivider>

          <ToggleListItem
            onClick={() => toggleNestedValue(brandFormPayload, 'options.red_alert', { shouldSetState: true })}
            toggleValue={red_alert}
          >
            <ToggleListItem.Title>Red Alert</ToggleListItem.Title>

            <ToggleListItem.Description>
              Brand will be set to Red Alert
            </ToggleListItem.Description>
          </ToggleListItem>
        </ScopedConditionalContent>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateBrand(updateBrandPayload)}
          size="large"
        >
          Save Brand Settings
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

BrandSettingsModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <BrandSettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
